import { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Divider,
  IconButton,
  Popover,
  Stack,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DecisionFields, {
  eventToDecisionForm,
  normalizeDecisionPayload,
  type DecisionFormValues,
} from '@/components/review/DecisionFields';
import VideoTimestampEditor from '@/components/review/VideoTimestampEditor';
import { LEFT_ANCHOR_POPOVER_ORIGINS, leftAnchorPopoverPaperSx } from '@/components/review/anchorPopoverSx';
import {
  REVIEW_EDIT_POPOVER_Z,
  reviewEditDialogZ,
  reviewEditMenuZ,
} from '@/components/review/reviewZIndex';

export type DecisionEventEditValues = ReturnType<typeof normalizeDecisionPayload> & {
  video_second?: number;
};

interface InitialValues {
  on_field_decision?: string | null;
  on_field_punishment?: string | null;
  reviewed_decision?: string | null;
  reviewed_punishment?: string | null;
  judgement?: string | null;
  note?: string | null;
  video_second?: number | null;
}

interface Props {
  open: boolean;
  anchorEl: HTMLElement | null;
  initial?: InitialValues;
  onClose: () => void;
  onSubmit: (values: DecisionEventEditValues) => void;
  showVideoTime?: boolean;
  videoDuration?: number;
  onPreviewTime?: (seconds: number) => void;
  popoverZIndex?: number;
  menuZIndex?: number;
  dialogZIndex?: number;
  dark?: boolean;
}

export default function DecisionEventEditPopover({
  open,
  anchorEl,
  initial,
  onClose,
  onSubmit,
  showVideoTime,
  videoDuration,
  onPreviewTime,
  popoverZIndex,
  menuZIndex,
  dialogZIndex,
  dark = false,
}: Props) {
  const resolvedPopoverZ = popoverZIndex ?? REVIEW_EDIT_POPOVER_Z;
  const resolvedMenuZ = menuZIndex ?? reviewEditMenuZ(resolvedPopoverZ);
  const resolvedDialogZ = dialogZIndex ?? reviewEditDialogZ(resolvedPopoverZ);

  const [form, setForm] = useState<DecisionFormValues>(eventToDecisionForm());
  const [videoSecond, setVideoSecond] = useState(0);

  useEffect(() => {
    if (open) {
      setForm(eventToDecisionForm(initial));
      setVideoSecond(initial?.video_second ?? 0);
    }
  }, [open, initial]);

  const handleSubmit = () => {
    const payload = normalizeDecisionPayload(form);
    if (showVideoTime) {
      const max = videoDuration && videoDuration > 0 ? videoDuration : videoSecond;
      onSubmit({ ...payload, video_second: Math.min(Math.max(0, videoSecond), max) });
    } else {
      onSubmit(payload);
    }
  };

  return (
    <Popover
      open={open && Boolean(anchorEl)}
      anchorEl={anchorEl}
      onClose={onClose}
      {...LEFT_ANCHOR_POPOVER_ORIGINS}
      disableScrollLock
      marginThreshold={8}
      slotProps={{
        root: { sx: { zIndex: resolvedPopoverZ } },
        paper: {
          sx: leftAnchorPopoverPaperSx(dark, { xs: 'min(calc(100vw - 32px), 360px)', sm: 400 }),
        },
      }}
    >
      <Stack sx={{ borderRadius: 'inherit' }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ px: 1.5, pt: 1, pb: 0.75 }}>
          <Typography variant="subtitle2" fontWeight={800} color={dark ? '#fff' : 'text.primary'}>
            編輯判決標記
          </Typography>
          <IconButton size="small" aria-label="關閉" onClick={onClose} sx={{ color: dark ? 'rgba(255,255,255,0.7)' : undefined }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Stack>

        <Divider sx={{ borderColor: dark ? 'rgba(255,255,255,0.12)' : undefined }} />

        <Box sx={{ px: 1.5, py: 1 }}>
          <Stack spacing={1}>
            {showVideoTime && (
              <VideoTimestampEditor
                value={videoSecond}
                onChange={setVideoSecond}
                duration={videoDuration}
                onPreview={onPreviewTime}
                dark={dark}
              />
            )}
            <DecisionFields
              values={form}
              onChange={(field, value) => setForm((v) => ({ ...v, [field]: value }))}
              dark={dark}
              compact
              menuZIndex={resolvedMenuZ}
              dialogZIndex={resolvedDialogZ}
            />
          </Stack>
        </Box>

        <Divider sx={{ borderColor: dark ? 'rgba(255,255,255,0.12)' : undefined }} />

        <Stack direction="row" spacing={1} sx={{ px: 1.5, py: 1 }}>
          <Button
            onClick={onClose}
            variant="outlined"
            fullWidth
            size="small"
            sx={dark ? { color: 'rgba(255,255,255,0.88)', borderColor: 'rgba(255,255,255,0.28)' } : undefined}
          >
            取消
          </Button>
          <Button onClick={handleSubmit} variant="contained" color="secondary" fullWidth size="small">
            儲存
          </Button>
        </Stack>
      </Stack>
    </Popover>
  );
}
